import { ErrorReturn, ErrorXMLParsing } from '../utils/ErrorReturn';
import {
  Claim,
  ClaimProvider,
  ClaimsExchange,
  ClaimsTransformation,
  DisplayClaim,
  InputClaim,
  OrchestrationStep,
  OutputClaim,
  PersistedClaim,
  PolicyComponents,
  Precondition,
  PreconditionAction,
  PreconditionType,
  ProviderSelect,
  ProviderSelections,
  NonInteractiveTechnicalProfile,
  SelfAssertedProfile,
  SubJourney,
  Transformation,
  TransformationInputParams,
  UserJourney,
  ValidationTP,
  CustomNode,
  Mask,
  AADAttribute,
} from './interfaces';
import { Edge, MarkerType } from 'react-flow-renderer';

export const init_SelfAssertedProfile = (): SelfAssertedProfile => {
  return {
    id: '',
    displayName: '',
    domain: '',
    description: '',
    protocol: ['', ''],
    inputClaims: [],
    displayClaims: [],
    outputClaims: [],
    validationTechnicalProfiles: [],
    inputClaimsTransformations: [],
    outputClaimsTransformations: [],
    metadata: [],
    claimProvider: '',
    includeTechnicalProfile: '',
  };
};

export const init_NonInteractiveTechnicalProfile = (): NonInteractiveTechnicalProfile => {
  return {
    id: '',
    displayName: '',
    domain: '',
    description: '',
    protocol: ['', ''],
    inputClaimsTransformations: [],
    inputClaims: [],
    persistedClaims: [],
    outputClaims: [],
    outputClaimsTransformations: [],
    metadata: [],
    claimProvider: '',
    includeTechnicalProfile: '',
  };
};

export const init_ClaimProvider = (): ClaimProvider => {
  return {
    displayName: '',
    domain: '',
  };
};

export const stringToPreconditionType = (str: string | null, xmlTrace: string): PreconditionType => {
  if (str === null) throw new ErrorXMLParsing('Type does not exist on Precondition Element', xmlTrace);

  switch (str) {
    case 'ClaimsExist':
      return PreconditionType.CLAIM_EXIST;
    case 'ClaimEquals':
      return PreconditionType.CLAIM_EQUALS;
    default:
      throw new ErrorXMLParsing('Invalid Type on Precondition, please check xml', xmlTrace);
  }
};

export const preconditionTypeToString = (preConType: PreconditionType): string => {
  switch (preConType) {
    case PreconditionType.CLAIM_EQUALS:
      return 'ClaimEquals';
    case PreconditionType.CLAIM_EXIST:
      return 'ClaimsExist';
    default:
      throw new ErrorReturn('Unrecognized PreconditionType');
  }
};

export const init_PreconditionTP = (): Precondition => {
  return {
    type: PreconditionType.CLAIM_EXIST,
    executeActionsIf: false,
    values: [],
    action: PreconditionAction.SKIPVALIDATIONTECHINCALPROFILE,
  };
};

export const init_PreconditionOrchestration = (): Precondition => {
  return {
    type: PreconditionType.CLAIM_EXIST,
    executeActionsIf: false,
    values: [],
    action: PreconditionAction.SKIPORCHESTRATIONSTEP,
  };
};

export const init_Mask = (): Mask => {
  return {
    type: 'Simple',
    regex: '',
    value: '',
  };
};

export const init_AADAttribute = (): AADAttribute => {
  return {
    id: '',
    dataType: 'string', // drop down , for now use string and boolean
  };
};

export const init_Claim = (): Claim => {
  return {
    id: '',
    displayName: '',
    dataType: 'string', // drop down , for now use string and boolean
    userInputType: '', // drop down, for now use textbox and multiselect,
    userHelpText: '',
    adminHelpText: '',
    userCreated: true,
  };
};

export const init_InputClaim = (): InputClaim => {
  return {
    claimTypeReferenceId: '',
    partnerClaimType: '',
    defaultValue: '',
    required: false,
    alwaysUseDefaultValue: false,
  };
};

export const init_OutputClaim = (): OutputClaim => {
  return {
    claimTypeReferenceId: '',
    partnerClaimType: '',
    defaultValue: '',
    required: false,
    alwaysUseDefaultValue: false,
  };
};

export const init_DisplayClaim = (): DisplayClaim => {
  return {
    claimTypeReferenceId: '',
    displayControlReferenceId: '',
    required: false,
  };
};

export const init_PersistedClaim = (): PersistedClaim => {
  return {
    claimTypeReferenceId: '',
    partnerClaimType: '',
    defaultValue: '',
  };
};

export const init_Transformation = (): Transformation => {
  return {
    claimReferenceId: '',
    transformationClaimType: '',
  };
};

export const init_TransformationInputParams = (): TransformationInputParams => {
  return {
    id: '',
    dataType: '',
    value: '',
  };
};

export const init_ClaimsTransformation = (): ClaimsTransformation => {
  return {
    id: '',
    transformationmethod: '',
    inputClaims: [],
    inputParameters: [],
    outputClaims: [],
  };
};

export const init_UserJourney = (): UserJourney => {
  return {
    id: '',
    defaultCpimIssuerTechnicalProfileReferenceId: '',
    authorizationTP: '',
    orchestrationSteps: [],
  };
};

export const init_SubJourney = (): SubJourney => {
  return {
    id: '',
    type: '',
    orchestrationSteps: [],
  };
};

export const init_OrchestrationStep = (): OrchestrationStep => {
  return {
    order: 0,
    type: '',
    contentDefinitonReferenceId: '',
    cpimIssuerTechnicalProfileReferenceId: '',
    preconditions: [],
    claimsProviderSelections: init_ProviderSelections(),
    claimsExchanges: [],
    subJourneyToInvoke: '',
  };
};

export const init_ClaimsExhange = (): ClaimsExchange => {
  return {
    id: '',
    technicalProfilerefrenceId: '',
  };
};

export const init_ValidationTP = (): ValidationTP => {
  return {
    id: '',
    continueOnError: false,
    continueOnSuccess: true,
    preconditions: [],
  };
};

export const init_ProviderSelections = (): ProviderSelections => {
  return {
    displayOptions: '',
    providers: [],
  };
};

export const init_ProviderSelect = (): ProviderSelect => {
  return {
    targetClaimExchangeId: '',
    validationClaimsExchange: '',
  };
};

export const init_PolicyComponents = (): PolicyComponents => {
  return {
    claims: {},
    claimProviders: [],
    claimTransformations: {},
    selfAssertedProfiles: {},
    otherProfiles: {},
    userJourneys: [],
    subJourneys: [],
    rawXMLString: '',
  };
};

export const init_Node = (): CustomNode => {
  return {
    parentId: '',
    id: '',
    type: 'process',
    position: { x: 0, y: 0 },
    data: {
      label: '',
      subJourney: '',
    },
  };
};
export const init_DiamondNode = (): CustomNode => {
  return {
    parentId: '',
    id: '',
    type: 'decision',
    position: { x: 0, y: 0 },
    data: {
      label: '',
    },
  };
};

//sourceHandle: 'right', starts edge at right
export const init_Edge = (): Edge => {
  return {
    id: '',
    source: '',
    target: '',
    type: 'smart',
    markerEnd: {
      type: MarkerType.ArrowClosed,
    },
  };
};
